/* eslint-disable @typescript-eslint/naming-convention */
// deps
import React, { memo, useContext, useCallback, useMemo } from "react";

// internal deps
import { GroupCartErrorBoundary, CartGroupOrderFooter, GroupCartTopBanner } from "@swiggy-private/cart-ui";
import { GenericError, RouteProps } from "@swiggy-private/core-ui";
import { UserSpecificDetails, ApiMenuCartItem } from "@swiggy-private/cart-types";
import { Toast } from "@swiggy-private/dls-web-recipe-food-web/dist/components/Toast";
import {
    CheckoutContext,
    closeWebview,
    CheckoutActionTypes,
    recordNREvent,
    NEWRELIC_TABLES,
    GroupOrderCartContext,
    GroupCartActionTypes,
} from "@swiggy-private/checkout-core";
import { CheckoutRoute } from "@swiggy-private/checkout-core";

// styles
import {
    CartGroupOrderItemContainer,
    PageContainer,
    TOAST_CONTAINER_CLASSNAME,
    ToastContainer,
} from "./GroupCartRenderer.style";

// components
import CartGroupOrderItem from "./CartGroupOrderItem";
import { UpdateProgressLoader } from "@swiggy-private/core-ui";
import GroupCartUIWrapper from "./group-cart-ui-wrapper/GroupCartUIWrapper";
import { default as Info } from "@swiggy-private/dls-web-icons/dist/web/generic/info/24";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";

// hooks
import useFetchGroupOrderCart from "@cart-client/hooks/useFetchGroupOrderCart";
import usePerformGroupOrderCartUpdate from "@cart-client/hooks/usePerformGroupOrderCartUpdate";
import useGroupCartPolling from "@cart-client/hooks/useGroupCartPolling";
import useLockGroupCart from "@cart-client/hooks/useLockGroupCartData";
import useUnlockGroupCart from "@cart-client/hooks/useUnlockGroupCart";
import useGroupCartAnalytics from "@cart-client/hooks/UseGroupCartAnalytics";

// helpers
import {
    isCurrentItemFromSameUser,
    isCurrentUserHost,
    onClickGroupCartErrorAction,
    sortUsersList,
} from "@cart-client/helpers/GroupCartOrder";
import { isEmpty } from "@swiggy-private/js-utils";

import CartUIWrapper from "@cart-client/components/cart-renderer/cart-ui-wrapper/CartUIWrapper";

// constants
import {
    GUEST_LOCKED_STATUS_BANNER_PROPS,
    GUEST_UNLOCKED_STATUS_BANNER_PROPS,
    HOST_BANNER_PROPS,
    HOST_UNLOCKING_TOAST_MESSAGE,
    NR_PAGE_ERROR_TYPES,
} from "@cart-client/constants/GroupOrder";

const GroupCartRenderer = (_props: RouteProps<CheckoutRoute>) => {
    const { state, setState } = useContext(GroupOrderCartContext);
    const { data: groupCartData, error, updateSharedCartLoading, softError, isCartLocked, diffString } = state;
    const { checkoutState } = useContext(CheckoutContext);
    const hostUserId = useMemo(() => {
        return groupCartData?.host_user_id;
    }, [groupCartData?.host_user_id]);

    const isHost = useMemo(() => isCurrentUserHost(hostUserId), [hostUserId]);

    const groupCartId = useMemo(() => {
        if (checkoutState.type === CheckoutActionTypes.SHARED_CART) return checkoutState?.payload?.groupCartId;
        return state.data?.group_cart_id || "";
    }, [checkoutState?.payload, checkoutState.type, state.data?.group_cart_id]);

    const analyticsProps = useGroupCartAnalytics(groupCartId);
    const { enablePolling, disablePolling } = useGroupCartPolling(groupCartId);
    const { getGroupOrderCartData } = useFetchGroupOrderCart(groupCartId, enablePolling, disablePolling);
    const { updateGroupOrderCartData } = usePerformGroupOrderCartUpdate(groupCartId, enablePolling, disablePolling);
    const { transitionToCart, checkoutInProgress } = useLockGroupCart(groupCartId, enablePolling, disablePolling);
    const { getUnlockedCartData, showUnlockToast, onDismissUnlockToast } = useUnlockGroupCart(
        hostUserId || "",
        groupCartId,
        enablePolling,
        disablePolling,
    );

    const updateGroupCart = useCallback(
        (userId: string) => (cartItems: ApiMenuCartItem[]) => {
            updateGroupOrderCartData(userId, cartItems);
        },
        [updateGroupOrderCartData],
    );

    const handleCheckoutClick = useCallback(() => {
        analyticsProps.clickCheckoutCtaEvent();
        transitionToCart(hostUserId as string);
    }, [analyticsProps, hostUserId, transitionToCart]);

    const sortedUsers = useMemo(() => {
        if (groupCartData?.user_cart_map) {
            return sortUsersList(groupCartData?.user_cart_map);
        }
    }, [groupCartData?.user_cart_map]);

    const resetSoftError = useCallback(() => {
        setState({
            type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
            payload: {
                softError: undefined,
            },
        });
    }, [setState]);

    const retryOnGenericError = useCallback(() => {
        // in case the unlocking has failed, then on click, unlocking should be retried
        if (hostUserId && checkoutState.type === CheckoutActionTypes.UNLOCK_GROUP_CART) {
            getUnlockedCartData(hostUserId, checkoutState.payload.nativeLocalMergedCart);
        }

        // in case the user's normal flow of shared cart has failed, then it should reload
        window.location.reload();
    }, [checkoutState.payload, checkoutState.type, getUnlockedCartData, hostUserId]);

    const onErrorCtaAction = useCallback(
        async (action: string) => {
            onClickGroupCartErrorAction(action, isHost, updateGroupOrderCartData, sortedUsers?.[0].user_cart);
        },
        [isHost, sortedUsers, updateGroupOrderCartData],
    );

    const onImpressionGenericError = useCallback(errPayload => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.GROUP_CART_GENERIC_ERROR_IMPRESSION,
            errType: NR_PAGE_ERROR_TYPES.CUSTOM_API_ERROR_CODE,
            ...errPayload,
        });
    }, []);

    const renderGenericError = useCallback(
        errPayload => {
            const _onImpressionGenericError = () => onImpressionGenericError(errPayload);
            const errorActionPayload = {
                onClick: retryOnGenericError,
            };
            return (
                <CartUIWrapper>
                    <GenericError
                        pageImpressionEvent={_onImpressionGenericError}
                        action={errorActionPayload}
                        title={error.titleMessage}
                        message={error.statusMessage}
                    />
                </CartUIWrapper>
            );
        },
        [error.statusMessage, error.titleMessage, onImpressionGenericError, retryOnGenericError],
    );

    const bannerProps = useMemo(() => {
        if (isHost) return HOST_BANNER_PROPS;
        return isCartLocked ? GUEST_LOCKED_STATUS_BANNER_PROPS : GUEST_UNLOCKED_STATUS_BANNER_PROPS;
    }, [isCartLocked, isHost]);

    return (
        <GroupCartErrorBoundary
            softError={softError}
            {...error}
            onClickBack={closeWebview}
            onErrorCtaAction={onErrorCtaAction}
            resetSoftError={resetSoftError}
            renderGenericError={renderGenericError}
        >
            <GroupCartUIWrapper
                isHost={isHost}
                groupCartId={groupCartId}
                doManualRefresh={getGroupOrderCartData}
                clickGrpCartItemEvent={analyticsProps.clickGrpCartItemEvent}
            >
                <PageContainer isHost={isHost}>
                    <CartGroupOrderItemContainer>
                        <GroupCartTopBanner {...bannerProps} />

                        {sortedUsers?.map((user: UserSpecificDetails, index: number) => {
                            const isCurrentItemFromCurrentUser = isCurrentItemFromSameUser(user?.user_details?.user_id);

                            return (
                                <CartGroupOrderItem
                                    groupCartId={groupCartId}
                                    key={user?.user_details?.user_id}
                                    isCurrentItemFromSameUser={isCurrentItemFromCurrentUser}
                                    userDetails={{ user_details: user.user_details, user_cart: user.user_cart }}
                                    isHost={isHost}
                                    hostUserId={hostUserId}
                                    updateGroupCart={updateGroupCart(user?.user_details?.user_id)}
                                    position={index}
                                    impressionGrpCartItem={analyticsProps.impressionGrpCartItem}
                                    clickGrpCartItemEvent={analyticsProps.clickGrpCartItemEvent}
                                />
                            );
                        })}
                    </CartGroupOrderItemContainer>

                    {(updateSharedCartLoading || checkoutInProgress) && <UpdateProgressLoader />}
                    <CartGroupOrderFooter
                        isHost={isHost}
                        onClick={handleCheckoutClick}
                        impressionEvent={analyticsProps.impressionCheckoutCta}
                        checkoutInProgress={checkoutInProgress}
                    />
                    {!isEmpty(diffString) && (
                        <ToastContainer isBottomSectionEmpty={!isHost}>
                            <Toast message={diffString} containerClass={TOAST_CONTAINER_CLASSNAME} autoDismiss show />
                        </ToastContainer>
                    )}
                    {showUnlockToast && (
                        <ToastContainer isBottomSectionEmpty={!isHost}>
                            <Toast
                                message={HOST_UNLOCKING_TOAST_MESSAGE}
                                leftAsset={<GenericIcon Icon={Info} />}
                                containerClass={TOAST_CONTAINER_CLASSNAME}
                                autoDismiss
                                onDismiss={onDismissUnlockToast}
                                show
                            />
                        </ToastContainer>
                    )}
                </PageContainer>
            </GroupCartUIWrapper>
        </GroupCartErrorBoundary>
    );
};

export default memo(GroupCartRenderer);
